import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  Button,
  Select,
  Box
} from '@chakra-ui/react';
import { AdminUsersType } from '../Table/MaintenanceAdminUsersTable';
import { UpdateAdminUsersApi } from '../../../../apis/UpdateMaintenance.api';
import { CustomNumberInput } from '../../../atoms/CustomNumberInput';
import {
  BanInputType,
  CustomStringInput
} from '../../../atoms/CustomStringInput';
import { AxiosError } from 'axios';

const MinPassword = 10;

interface props {
  isOpen: boolean;
  onClose: () => void;
  EditData: AdminUsersType | undefined;
  setMaintenanceDatas: React.Dispatch<
    React.SetStateAction<AdminUsersType[] | undefined>
  >;
  CreateOrUpdateStateSwitch: () => void;
}

interface ResponseType {
  success: boolean;
  data: AdminUsersType[] | null;
  message: unknown | null;
}

const RoleEnum = {
  MastaMente: 1,
  Notice: 2,
  Browse: 3,
  Suspension: 4
};

const GetRoleNumber = (role: string | undefined) => {
  switch (role) {
    case 'マスタメンテ':
      return RoleEnum.MastaMente;
    case '通知':
      return RoleEnum.Notice;
    case '閲覧':
      return RoleEnum.Browse;
    case '利用停止':
      return RoleEnum.Suspension;
    default:
      return RoleEnum.MastaMente;
  }
};

const GetCrewNumberInputCheckData = (addData: string | undefined) => {
  if (addData === undefined) {
    return { ViewInt: 10, ViewDigit: 0, Value: '' };
  }
  return { ViewInt: 10, ViewDigit: 0, Value: addData };
};

const GetCrewNameInputCheckData = (addData: string | undefined) => {
  if (addData === undefined) {
    return { Length: 300, BanInputType: BanInputType.None, Value: '' };
  }
  return { Length: 300, BanInputType: BanInputType.None, Value: addData };
};

const GetFuriganaInputCheckData = (addData: string | undefined) => {
  if (addData === undefined) {
    return { Length: 300, BanInputType: BanInputType.None, Value: '' };
  }
  return { Length: 300, BanInputType: BanInputType.None, Value: addData };
};

const GetPasswordInputCheckData = (addData: string | undefined) => {
  if (addData === undefined) {
    return { Length: 40, BanInputType: BanInputType.FullWidth, Value: '' };
  }
  return { Length: 40, BanInputType: BanInputType.FullWidth, Value: addData };
};

const CheckPassword = (
  password: string | undefined,
  checkPassword: string | undefined
) => {
  if (password === undefined || password === '') {
    alert('パスワードを入力して下さい。');
    return false;
  } else if (password !== checkPassword) {
    alert('パスワードの入力が一致しません。');
    return false;
  } else if (password?.length < MinPassword) {
    alert('パスワードは10桁以上で入力して下さい。');
    return false;
  } else if (
    !password?.match(
      /^(?=.*[a-z])(?=.*[0-9])(?=.*[!-/:-@¥[-`{-~])[a-z0-9!-/:-@¥[-`{-~]+$/
    )
  ) {
    alert('パスワードに半角英数字記号が含まれていません。');
    return false;
  }
  return true;
};

const AdminUsersEditWindow: React.FC<props> = (props) => {
  const {
    isOpen,
    onClose,
    EditData,
    setMaintenanceDatas,
    CreateOrUpdateStateSwitch
  } = props;
  const [crewNumber, setCrewNumber] = useState(EditData?.user_no?.toString());
  const [crewName, setCrewName] = useState(EditData?.name);
  const [furigana, setFurigana] = useState(EditData?.kana);
  const [password, setPassword] = useState<string | undefined>('');
  const [checkPassword, setCheckPassword] = useState<string | undefined>('');
  const [role, setRole] = useState(GetRoleNumber(EditData?.role));

  const PostApi = async () => {
    if (CheckPassword(password, checkPassword) === false) {
      return;
    }
    if (EditData !== undefined && EditData.id !== undefined) {
      const response: ResponseType = await UpdateAdminUsersApi(EditData.id, {
        user_no: crewNumber,
        name: crewName,
        kana: furigana,
        password: password,
        role: Number(role)
      });

      if (
        response.message !== null &&
        (response.message as AxiosError).status?.toString() === '409'
      ) {
        alert('既に登録されている乗務員番号です。');
        return;
      } else if (response?.success === false) {
        alert('編集に失敗しました。');
        return;
      }

      setMaintenanceDatas(response.data as AdminUsersType[]);
      CreateOrUpdateStateSwitch();
      onClose();
    }
  };
  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent h='460px' maxW='1100px'>
          <ModalHeader
            textAlign='center'
            bg='gray.800'
            color='white'
            fontSize='16'
          >
            乗務員を編集
          </ModalHeader>
          <ModalCloseButton color='white' />
          <ModalBody>
            <Box
              position='relative'
              display='grid'
              gridTemplateColumns='1fr 6fr'
            >
              <Text mt={4}>乗務員番号*</Text>
              <CustomNumberInput
                CheckInfo={GetCrewNumberInputCheckData(
                  EditData?.user_no?.toString()
                )}
                inputValue={crewNumber}
                setValue={setCrewNumber}
              />

              <Text mt={4}>乗務員名*</Text>
              <CustomStringInput
                CheckInfo={GetCrewNameInputCheckData(
                  EditData?.name?.toString()
                )}
                inputValue={crewName}
                setValue={setCrewName}
                PhohibitionCharaCheck={false}
              />

              <Text mt={4}>フリガナ</Text>
              <CustomStringInput
                CheckInfo={GetFuriganaInputCheckData(
                  EditData?.kana?.toString()
                )}
                inputValue={furigana}
                setValue={setFurigana}
                PhohibitionCharaCheck={false}
              />

              <Text mt={4}>パスワード*</Text>
              <CustomStringInput
                CheckInfo={GetPasswordInputCheckData('')}
                inputValue={password}
                setValue={setPassword}
                PhohibitionCharaCheck={false}
              />

              <Text mt={4}>確認パスワード*</Text>
              <CustomStringInput
                CheckInfo={GetPasswordInputCheckData('')}
                inputValue={checkPassword}
                setValue={setCheckPassword}
                PhohibitionCharaCheck={false}
              />

              <Text mt={4}>管理権限*</Text>
              <Select
                mt={2}
                w='20%'
                value={role}
                onChange={(e) => {
                  setRole(Number(e.target.value));
                }}
              >
                <option value={RoleEnum.MastaMente}>マスタメンテ</option>
                <option value={RoleEnum.Notice}>通知</option>
                <option value={RoleEnum.Browse}>閲覧</option>
                <option value={RoleEnum.Suspension}>利用停止</option>
              </Select>
            </Box>
            <Button
              mt={10}
              onClick={() => {
                PostApi();
              }}
            >
              更新
            </Button>
            <Button mt={10} ml={10} onClick={() => onClose()}>
              取り消す
            </Button>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default React.memo(AdminUsersEditWindow);
